import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MantineProvider, createTheme } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { App } from "./App.js";
import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/carousel/styles.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./styles.css";
import { AuthProvider } from "./contexts/AuthContext.js";

const brand = [
  "#f0f4f4",
  "#e1e9ea",
  "#c8d7da",
  "#adc3c7",
  "#91adb2",
  "#78969c",
  "#637d83",
  "#4e656b",
  "#394f55",
  "#263b41",
] as const;

const discord = [
  "#eef0f5",
  "#dfe3ed",
  "#c8cedd",
  "#aeb7ce",
  "#939fbe",
  "#7987aa",
  "#64718f",
  "#4e5a76",
  "#39445e",
  "#263049",
] as const;

const dark = [
  "#f5f4f0",
  "#e3e2dc",
  "#c9c9c2",
  "#adafaa",
  "#929590",
  "#777b77",
  "#5a5e5b",
  "#292c2b",
  "#1c1f1e",
  "#111313",
] as const;

const orange = [
  "#f7f0e3", "#eddfc4", "#decca2", "#cdb581", "#baa064",
  "#9d844c", "#806d3f", "#625638", "#493f2f", "#352f27",
] as const;

const yellow = [
  "#f7f1df", "#ecdfb8", "#ddcc8f", "#cbb66e", "#b29b55",
  "#927c42", "#756538", "#5b4f31", "#443d2b", "#342f26",
] as const;

const green = [
  "#edf4ed", "#d9e8da", "#c0d8c2", "#a5c5a9", "#89af8e",
  "#729678", "#5d7c62", "#49614e", "#374b3b", "#293a2d",
] as const;

const teal = [
  "#ecf4f2", "#d9e9e4", "#bed8d1", "#a2c4ba", "#84aea2",
  "#6b9187", "#55776e", "#425f59", "#324a46", "#263a38",
] as const;

const red = [
  "#f7ecea", "#ecd8d5", "#dfc0bc", "#d1a6a1", "#c18c86",
  "#a9706b", "#8b5a56", "#6b4744", "#503835", "#3c2d2c",
] as const;

const mantineTheme = createTheme({
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  primaryColor: "brand",
  colors: {
    brand,
    discord,
    dark,
    blue: brand,
    cyan: brand,
    violet: brand,
    grape: brand,
    orange,
    yellow,
    green,
    teal,
    red,
  },
  radius: {
    xs: "4px",
    sm: "5px",
    md: "8px",
    lg: "11px",
    xl: "14px",
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof Error && "status" in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");

createRoot(container).render(
  <React.StrictMode>
    <MantineProvider theme={mantineTheme} defaultColorScheme="dark">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ModalsProvider>
            <Notifications position="top-right" />
            <App />
          </ModalsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  </React.StrictMode>,
);
